/**
 * `Artist` — the artist page, and the shared `DetailView` `Tag.tsx` renders through
 * too. `docs/design-direction.md` §8.6, `spec.md` §6.4 (`GET /api/artists/:id`).
 *
 * ## The loud object, and the quiet one it earns
 *
 * A hand-painted `SpeakerStack` — the artist's name split one word per panel, each
 * word stretched to fill its cabinet (`components/SpeakerStack.tsx` owns the CSS
 * flare and the bass-bin cutout; this file only sizes it, in `.stack`). It is the
 * loudest typography in the app, and the only colour behind it is two 10%
 * `--accent-quiet` conic gradients — which is what earns the ink (§2, §12.13). The
 * rest of the screen is a 1px-divided amp-faceplate readout, a block of prose on a
 * 28px baseline, a downbeat rule and a ledger. **Exactly one thing on the screen
 * inverts to solid `--paper`: `PLAY ALL`** (`.playAll`, `data-inverted="true"`), which
 * is the whole of acceptance criterion 4.
 *
 * ## `DetailView` — one layout, two screens
 *
 * §8.6's last sentence is explicit: "Tag views use the identical layout with a
 * one-line definition in place of the biography." Rather than duplicate the hero,
 * the faceplate, the prose block and the ledger into `Tag.tsx`, this file exports
 * {@link DetailView} — a presentational component that knows nothing about which
 * endpoint fed it — and `Tag.tsx` imports it, along with {@link FaceplateStrip},
 * {@link groupDiscography} and the copy constants. `Artist` and `Tag` are the two
 * thin fetch-and-adapt shells; `DetailView` is the one screen.
 *
 * ## Every honesty rule this screen keeps
 *
 *  · **The prose block is entirely absent when there is nothing to print** (§12.6,
 *    plan.md T45 acceptance 1). `bio` is `null` and `sources` is `[]` in v1 — there is
 *    no data source yet (`schemas/catalogue.ts`'s own comment on
 *    `ArtistDetailResponseSchema`) — and {@link DetailView} does not render a
 *    heading, a placeholder rule or an empty paragraph for that case: `hasProse` is
 *    the one gate, and nothing inside it renders when it is `false`.
 *  · **The faceplate omits a cell it cannot compute, rather than dashing it**
 *    (§12.6, acceptance 5). {@link artistFaceplate} and {@link tagFaceplate} pass
 *    `null` for a fact the response does not carry — a tag has no `riddimCount` and
 *    no `durationS` at all (`TagDetailResponseSchema` carries neither) — and
 *    {@link FaceplateStrip} reuses `SpecTable`'s own `isMissing`, so the same
 *    anti-fabrication rule governs both plates in this app.
 *  · **`DISCOGRAPHY` is grouped by label from a real field, never a fabricated one.**
 *    A `TrackSummary` carries no `label` string — `spec.md` §5.2 keeps labels as
 *    `tags` rows, and the nested `tracks` page this screen renders is `TrackSummary`,
 *    which carries no `tags` array at all (only `TrackDetail` does). What every track
 *    *does* carry is `catalogueNo`, and `CatalogueNo`'s own schema pins it to
 *    `^BL-\d{4}$` — so {@link catalogueGroupLabel} groups by the imprint prefix before
 *    the dash, which is real, printed-on-every-sleeve data. On today's catalogue that
 *    is always `BL`, because there is one imprint; the grouping is written against the
 *    field rather than against that fact, so it keeps working the day a second prefix
 *    exists rather than needing to be rediscovered.
 *  · **A track with no known year still sorts and still prints** — its year cell is
 *    blank, not a fabricated one, and {@link groupDiscography} sorts an unknown year
 *    to the end of its group rather than guessing a position for it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import type { ArtistSource, ArtistStats, TrackSummary } from '@bl/shared';

import type { RouteName, RouteParams } from '../app/router';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { formatTimecode } from '../components/Scrub';
import { SpeakerStack } from '../components/SpeakerStack';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import { isMissing } from '../components/primitives/SpecTable';
import type { SpecValue } from '../components/primitives/SpecTable';
import type { ApiClient, ApiFailure } from '../lib/api';
import { USER_MESSAGE_RU, api, displayCode, isApiErrorCode, toApiFailure } from '../lib/api';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';

import styles from './artist.module.css';

/*
 * An artist/tag whose route id is invalid renders through `ErrorState`, not
 * `Empty`: the closed set of `Empty` variants (`crate` | `search` | `queue` |
 * `favorites`, `app/states/Empty.tsx`) has no shape for "this id does not exist",
 * and inventing a fifth variant is exactly what that file's frozen copy pool
 * exists to prevent.
 */

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** English label print, licensed by §11; the accessible name is the Russian verb. */
export const PLAY_ALL = Object.freeze({ print: 'PLAY ALL', ru: 'Играть всё' });

/** `DISCOGRAPHY`'s own accessible name is unnecessary — it is static print, read as-is. */
export const DISCOGRAPHY_LABEL = 'DISCOGRAPHY';

export const LOAD_MORE_RU = 'ещё';

/** The amp-faceplate's Russian labels. Sentence case: §4.3, §12.16 (see the guard below). */
export const FACEPLATE_LABELS_RU = Object.freeze({
  tracks: 'Треков',
  years: 'Годы',
  riddims: 'Риддимов',
});

export const ARTIST_SCREEN_LABEL_RU = 'Артист';
export const TAG_SCREEN_LABEL_RU = 'Тег';

/* ══ pure helpers ══════════════════════════════════════════════════════════════════ */

/** A route param as a positive integer id, or `null`. A URL has no numbers in it. */
export function parseRouteId(value: string | undefined): number | null {
  if (value === undefined) return null;
  if (!/^\d+$/u.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** The failure printed when a route id is not a real id — never worth retrying. */
export function invalidIdFailure(): ApiFailure {
  return {
    code: 'NOT_FOUND',
    message: USER_MESSAGE_RU.NOT_FOUND,
    displayCode: displayCode('NOT_FOUND'),
    status: 0,
    retryable: false,
  };
}

/**
 * The imprint prefix of a catalogue number — the part before the dash — which is
 * the one real, printed-on-every-sleeve fact a `TrackSummary` carries about where it
 * was cut (see this file's header comment for why this is the field DISCOGRAPHY
 * groups by, not a fabricated one).
 */
export function catalogueGroupLabel(catalogueNo: string): string {
  const dash = catalogueNo.indexOf('-');
  if (dash <= 0) return catalogueNo;
  const prefix = catalogueNo.slice(0, dash).trim();
  return prefix === '' ? catalogueNo : prefix;
}

/** `1994–2004`, or the single year, or `null` when either end is unknown. */
export function yearRangeText(from: number | null, to: number | null): string | null {
  if (from === null || to === null) return null;
  return from === to ? String(from) : `${String(from)}–${String(to)}`;
}

/**
 * A liner-note string split into paragraphs on blank lines. `null` — the v1 shape
 * for "no data source" — becomes the empty array, which is what tells
 * {@link DetailView} to omit the whole block.
 */
export function splitProse(text: string | null): readonly string[] {
  if (text === null) return [];
  return text
    .split(/\n{2,}/u)
    .map((part) => part.replace(/\s+/gu, ' ').trim())
    .filter((part) => part !== '');
}

/** One cell of the amp-faceplate strip. `null`/`undefined` means "omit", not "dash". */
export interface FaceplateCell {
  readonly key: string;
  readonly label: string | null;
  readonly value: SpecValue;
}

/** `ТРЕКОВ 42 · ГОДЫ 1994–2004 · РИДДИМОВ 7 · 1:38:04` — every fact `ArtistStats` has. */
export function artistFaceplate(stats: ArtistStats): readonly FaceplateCell[] {
  return [
    { key: 'tracks', label: FACEPLATE_LABELS_RU.tracks, value: stats.trackCount },
    {
      key: 'years',
      label: FACEPLATE_LABELS_RU.years,
      value: yearRangeText(stats.yearFrom, stats.yearTo),
    },
    { key: 'riddims', label: FACEPLATE_LABELS_RU.riddims, value: stats.riddimCount },
    { key: 'duration', label: null, value: formatTimecode(stats.durationS) },
  ];
}

/**
 * A tag's faceplate — two cells, because `TagDetailResponseSchema` carries no
 * `riddimCount` and no `durationS` at all. Those two cells are simply not
 * constructed rather than constructed with a `null` value that happens to render the
 * same way: there is no fact to omit, because there is no field to have read it from.
 */
export function tagFaceplate(
  trackCount: number,
  firstYear: number | null,
  lastYear: number | null,
): readonly FaceplateCell[] {
  return [
    { key: 'tracks', label: FACEPLATE_LABELS_RU.tracks, value: trackCount },
    { key: 'years', label: FACEPLATE_LABELS_RU.years, value: yearRangeText(firstYear, lastYear) },
  ];
}

/* ══ discography grouping ══════════════════════════════════════════════════════════ */

export interface DiscographyEntry {
  readonly track: TrackSummary;
  /** This entry's position in the flat `tracks` array — what the queue is built from. */
  readonly index: number;
}

export interface DiscographyGroup {
  readonly label: string;
  readonly entries: readonly DiscographyEntry[];
}

/**
 * Groups a flat track list by {@link catalogueGroupLabel}, sorted within a group by
 * year (unknown years last) then title, and groups sorted alphabetically by label.
 *
 * `index` on every entry is the position in the *original* `tracks` array, which is
 * the order `queue.replace` plays back — a tap inside a re-sorted group still starts
 * the queue at the right place because it carries that original index forward, not a
 * position inside the sorted, grouped display order.
 */
export function groupDiscography(tracks: readonly TrackSummary[]): readonly DiscographyGroup[] {
  const buckets = new Map<string, DiscographyEntry[]>();
  tracks.forEach((track, index) => {
    const label = catalogueGroupLabel(track.catalogueNo);
    const bucket = buckets.get(label);
    if (bucket === undefined) buckets.set(label, [{ track, index }]);
    else bucket.push({ track, index });
  });

  const groups = [...buckets.entries()].map(([label, entries]) => ({
    label,
    entries: [...entries].sort((a, b) => {
      const ay = a.track.year ?? Number.POSITIVE_INFINITY;
      const by = b.track.year ?? Number.POSITIVE_INFINITY;
      if (ay !== by) return ay - by;
      return a.track.title.localeCompare(b.track.title);
    }),
  }));

  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}

/** `label value`, or bare `value` when the cell has no label (the duration cell). */
function cellText(label: string | null, value: SpecValue): string {
  const rendered = String(value);
  return label === null ? rendered : `${label} ${rendered}`;
}

/* Whether a string carries a Cyrillic code point — the same guard `SpecTable`,
   `DownbeatRule` and `Silkscreen` all apply before forcing the mono tier's caps and
   tracking (§4.3, §12.16). Kept local rather than imported from `@bl/shared` a second
   time under a different name would be pointless; it IS `hasCyrillic` re-declared
   only because this file already imports enough from `@bl/shared` — inlined instead
   to keep the import list honest about what it actually re-exports. */
function hasCyrillicText(value: string): boolean {
  return /\p{Script=Cyrillic}/u.test(value);
}

/* ══ FaceplateStrip — shared by Artist and Tag ═══════════════════════════════════ */

export interface FaceplateStripProps {
  readonly cells: readonly FaceplateCell[];
  readonly className?: string | undefined;
}

/**
 * The amp-faceplate readout: mono cells divided by 1px verticals, one per known
 * fact. Reuses `SpecTable`'s `isMissing` so "omit an unknown cell, never a dash"
 * is one rule shared by both plates in the app, not two copies of it.
 */
export function FaceplateStrip({ cells, className }: FaceplateStripProps): JSX.Element | null {
  const printable = cells.filter((cell) => !isMissing(cell.value));
  if (printable.length === 0) return null;

  return (
    <div
      className={cx(styles.faceplate, className)}
      role="group"
      aria-label="Сведения"
      data-faceplate="true"
    >
      {printable.map((cell) => {
        const text = cellText(cell.label, cell.value);
        return (
          <span
            key={cell.key}
            className={cx(
              't-mono',
              't-13',
              styles.faceplateCell,
              hasCyrillicText(text) && styles.cyr,
            )}
            data-cell={cell.key}
          >
            {text}
          </span>
        );
      })}
    </div>
  );
}

/* ══ the discography ledger — shared by Artist and Tag ════════════════════════════ */

interface DiscographyRowProps {
  readonly entry: DiscographyEntry;
  readonly current: boolean;
  readonly onPlay: () => void;
}

function DiscographyRow({ entry, current, onPlay }: DiscographyRowProps): JSX.Element {
  const { track } = entry;
  const name =
    track.performer === null || track.performer === ''
      ? track.title
      : `${track.title}, ${track.performer}`;

  return (
    <li className={styles.discRow} data-current={current ? 'true' : 'false'}>
      <button
        type="button"
        className={styles.discButton}
        aria-current={current ? true : undefined}
        aria-label={name}
        onClick={onPlay}
      >
        <span className={cx('t-mono', 't-11', styles.discYear)}>
          {track.year === null ? '' : String(track.year)}
        </span>
        <span className={cx('t-ui', 't-15', 't-clamp-1', styles.discTitle)}>{track.title}</span>
        <span className={cx('t-mono', 't-11', styles.discCat)}>{track.catalogueNo}</span>
      </button>
    </li>
  );
}

interface DiscographyListProps {
  readonly groups: readonly DiscographyGroup[];
  readonly currentTrackId: number | null;
  readonly onPlay: (index: number) => void;
}

function DiscographyList({ groups, currentTrackId, onPlay }: DiscographyListProps): JSX.Element {
  return (
    <ul className={styles.discList} data-discography="true">
      {groups.map((group) => (
        <li key={group.label} className={styles.discGroup}>
          <span className={cx('t-mono', 't-11', styles.discGroupHeader)}>{group.label}</span>
          <ul className={styles.discGroupRows}>
            {group.entries.map((entry) => (
              <DiscographyRow
                key={entry.track.id}
                entry={entry}
                current={currentTrackId === entry.track.id}
                onPlay={() => {
                  onPlay(entry.index);
                }}
              />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/* ══ DetailView — the one screen Artist and Tag both render ══════════════════════ */

export interface DetailViewProps {
  /** Drives `data-screen` — the only visible difference between the two callers. */
  readonly dataScreen: 'artist' | 'tag';
  /** The landmark's accessible name: "Артист" or "Тег", never the record's own name
      (matching `NowPlaying`'s `SCREEN_LABEL` precedent — a screen name, not a title). */
  readonly screenLabel: string;
  /** The artist or tag name — what `SpeakerStack` cuts one word per panel. */
  readonly heroName: string;
  readonly faceplate: readonly FaceplateCell[];
  /** Already split into paragraphs; `[]` when there is nothing to print. */
  readonly proseParagraphs: readonly string[];
  /** `[]` for a tag — `TagDetailResponseSchema` carries no sources at all. */
  readonly sources: readonly ArtistSource[];
  readonly tracks: readonly TrackSummary[];
  readonly currentTrackId: number | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onLoadMore: () => void;
  readonly onPlayAll: () => void;
  readonly onPlayTrack: (index: number) => void;
}

export function DetailView({
  dataScreen,
  screenLabel,
  heroName,
  faceplate,
  proseParagraphs,
  sources,
  tracks,
  currentTrackId,
  hasMore,
  loadingMore,
  onLoadMore,
  onPlayAll,
  onPlayTrack,
}: DetailViewProps): JSX.Element {
  const groups = useMemo(() => groupDiscography(tracks), [tracks]);
  const hasProse = proseParagraphs.length > 0 || sources.length > 0;
  const hasTracks = tracks.length > 0;

  return (
    <div className={styles.screen} data-screen={dataScreen}>
      <StateFrame grid="ledger" role="group" ariaLabel={screenLabel}>
        <div className={styles.head}>
          <h1 className={styles.heroWrap}>
            <SpeakerStack name={heroName} className={styles.stack} />
          </h1>

          <FaceplateStrip cells={faceplate} />

          {hasProse ? (
            <div className={styles.prose} data-prose="true">
              {proseParagraphs.map((paragraph, index) => (
                <p key={index} className={cx('t-ui', 't-15', styles.proseP)}>
                  {paragraph}
                </p>
              ))}

              {sources.length === 0 ? null : (
                <ul className={styles.sources}>
                  {sources.map((source) => (
                    <li key={source.url}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={cx(
                          't-mono',
                          't-11',
                          styles.citation,
                          hasCyrillicText(source.label) && styles.cyr,
                        )}
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        {hasTracks ? (
          <>
            <DownbeatRule />

            <div className={styles.discoHeader} data-sticky="true">
              <Halftone as="div" className={styles.discoHeaderHalo}>
                <span className={cx('t-mono', 't-13', styles.discoTitle)}>{DISCOGRAPHY_LABEL}</span>
              </Halftone>

              <button
                type="button"
                className={styles.playAll}
                data-inverted="true"
                aria-label={PLAY_ALL.ru}
                onClick={onPlayAll}
              >
                {PLAY_ALL.print}
              </button>
            </div>

            <DiscographyList groups={groups} currentTrackId={currentTrackId} onPlay={onPlayTrack} />

            {hasMore ? (
              <button
                type="button"
                className={styles.loadMore}
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {LOAD_MORE_RU}
              </button>
            ) : null}
          </>
        ) : null}
      </StateFrame>
    </div>
  );
}

/* ══ Artist — the fetch-and-adapt shell ═══════════════════════════════════════════ */

export type ArtistClient = Pick<ApiClient, 'artist'>;

export interface ArtistDeps {
  readonly client?: ArtistClient;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
  readonly telegram?: TelegramFacade;
}

export interface ArtistProps {
  readonly route?: RouteName;
  readonly params?: RouteParams;
  readonly deps?: ArtistDeps;
}

interface TracksPage {
  readonly items: readonly TrackSummary[];
  readonly nextCursor: string | null;
}

const EMPTY_TRACKS_PAGE: TracksPage = Object.freeze({ items: [], nextCursor: null });

type LoadStatus = 'loading' | 'ready' | 'error';

export function Artist({ params = {}, deps = {} }: ArtistProps): JSX.Element {
  const client = deps.client ?? api;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;
  const tg = deps.telegram ?? defaultTelegram;

  const artistId = useMemo(() => parseRouteId(params['id']), [params]);

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [error, setError] = useState<ApiFailure | null>(null);
  const [name, setName] = useState<string>('');
  const [stats, setStats] = useState<ArtistStats | null>(null);
  const [bio, setBio] = useState<string | null>(null);
  const [sources, setSources] = useState<readonly ArtistSource[]>([]);
  const [tracksPage, setTracksPage] = useState<TracksPage>(EMPTY_TRACKS_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);

  const currentTrackId = player((state) => state.trackId);
  const liveIdRef = useRef<number | null>(null);
  /** Bumped by `retry` — the fetch effect below depends on it, which is what makes a
      re-tap of `ЕЩЁ РАЗ` actually re-issue the request rather than only resetting
      local state that nothing then reads from again. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (artistId === null) {
      setStatus('error');
      setError(invalidIdFailure());
      return;
    }

    liveIdRef.current = artistId;
    const controller = new AbortController();
    let live = true;
    setStatus('loading');
    setError(null);

    client
      .artist(artistId, undefined, { signal: controller.signal })
      .then((response) => {
        if (!live) return;
        setName(response.artist.name);
        setStats(response.stats);
        setBio(response.bio);
        setSources(response.sources);
        setTracksPage({ items: response.tracks.items, nextCursor: response.tracks.nextCursor });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (!live || isApiErrorCode(err, 'ABORTED')) return;
        setStatus('error');
        setError(toApiFailure(err));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, artistId, reloadToken]);

  const loadMore = useCallback((): void => {
    const id = liveIdRef.current;
    if (id === null || tracksPage.nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    client
      .artist(id, { cursor: tracksPage.nextCursor })
      .then((response) => {
        setTracksPage((previous) => ({
          items: [...previous.items, ...response.tracks.items],
          nextCursor: response.tracks.nextCursor,
        }));
      })
      .catch(() => {
        /* A failed "load more" leaves the ledger exactly as it was — no error plate
           over a screen that is already showing real content (§8.8). */
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [client, loadingMore, tracksPage.nextCursor]);

  const playAll = useCallback((): void => {
    tg.haptics.impact('rigid');
    const w = deps.wiring ?? getWiring();
    void queue
      .getState()
      .replace(tracksPage.items, 0)
      .then(() => w.select(0));
  }, [deps.wiring, queue, tg, tracksPage.items]);

  const playTrack = useCallback(
    (index: number): void => {
      tg.haptics.impact('rigid');
      const w = deps.wiring ?? getWiring();
      void queue
        .getState()
        .replace(tracksPage.items, index)
        .then(() => w.select(index));
    },
    [deps.wiring, queue, tg, tracksPage.items],
  );

  const retry = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  if (status === 'error') {
    return (
      <div className={styles.screen} data-screen="artist">
        <ErrorState
          failure={error ?? invalidIdFailure()}
          grid="ledger"
          onRetry={retry}
          className={styles.error}
        />
      </div>
    );
  }

  if (status === 'loading' || stats === null) {
    return (
      <div className={styles.screen} data-screen="artist">
        <Loading grid="ledger" rows={4} />
      </div>
    );
  }

  return (
    <DetailView
      dataScreen="artist"
      screenLabel={ARTIST_SCREEN_LABEL_RU}
      heroName={name}
      faceplate={artistFaceplate(stats)}
      proseParagraphs={splitProse(bio)}
      sources={sources}
      tracks={tracksPage.items}
      currentTrackId={currentTrackId}
      hasMore={tracksPage.nextCursor !== null}
      loadingMore={loadingMore}
      onLoadMore={loadMore}
      onPlayAll={playAll}
      onPlayTrack={playTrack}
    />
  );
}
